import React, { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import BillingGraceBanner from './BillingGraceBanner';
import Waitlist from '../pages/Waitlist';
import Signup from '../pages/Signup';
import PaymentRequired from '../pages/PaymentRequired';
import { getContractApiUrl } from '../utils/apiUrls';
import { portalApiTokenOptions } from '../utils/authSession';

const BACKEND_URL = getContractApiUrl();

const referralStorageKey = (email, key) => (
    `keyflo_referral_${key}_${(email || '').toLowerCase().trim() || 'anonymous'}`
);

function referralParamsFromSearch(search) {
    const params = new URLSearchParams(search || '');
    return {
        token: params.get('referral_token') || '',
        sendId: params.get('referral_send_id') || '',
    };
}

const Gatekeeper = ({ children }) => {
    const { user, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
    const [accessData, setAccessData] = useState(null);
    const [checkingAccess, setCheckingAccess] = useState(true);
    const location = useLocation();
    const referralParams = referralParamsFromSearch(location.search);

    const fetchAccessStatus = useCallback(async () => {
        if (isLoading || !isAuthenticated || !user?.email) {
            return null;
        }

        const headers = {};
        try {
            const token = await getAccessTokenSilently(portalApiTokenOptions());
            if (token) headers.Authorization = `Bearer ${token}`;
        } catch (_) {
            /* local dev may run without audience */
        }

        const normalizedEmail = user.email.toLowerCase().trim();
        const params = new URLSearchParams({ email: normalizedEmail });
        const referralToken = referralParams.token;
        const referralSendId = referralParams.sendId;
        let persistedReferralToken = referralToken;
        let persistedReferralSendId = referralSendId;
        if (typeof window !== 'undefined') {
            const tokenStorageKey = referralStorageKey(normalizedEmail, 'token');
            const sendStorageKey = referralStorageKey(normalizedEmail, 'send_id');
            if (referralToken) {
                window.sessionStorage.setItem(tokenStorageKey, referralToken);
            } else {
                persistedReferralToken = window.sessionStorage.getItem(tokenStorageKey);
            }
            if (referralSendId) {
                window.sessionStorage.setItem(sendStorageKey, referralSendId);
            } else {
                persistedReferralSendId = window.sessionStorage.getItem(sendStorageKey);
            }
        }
        if (persistedReferralToken) {
            params.set('referral_token', persistedReferralToken);
        }
        if (persistedReferralSendId) {
            params.set('referral_send_id', persistedReferralSendId);
        }

        const response = await axios.get(`${BACKEND_URL}/user/access-status?${params.toString()}`, { headers });
        return response.data;
    }, [
        user,
        isAuthenticated,
        isLoading,
        getAccessTokenSilently,
        referralParams.token,
        referralParams.sendId,
    ]);

    useEffect(() => {
        let cancelled = false;

        const loadInitialAccess = async () => {
            if (isLoading) {
                return;
            }
            if (!isAuthenticated || !user?.email) {
                setCheckingAccess(false);
                return;
            }

            setCheckingAccess(true);
            try {
                const data = await fetchAccessStatus();
                if (!cancelled) {
                    if (data?.status) {
                        setAccessData(data);
                    } else {
                        setAccessData((prev) => prev || { status: 'UNAVAILABLE' });
                    }
                }
            } catch (error) {
                console.error('Error checking access status:', error);
                if (!cancelled) {
                    // Pool timeouts / 5xx are not an entitlement decision.
                    setAccessData((prev) => prev || { status: 'UNAVAILABLE' });
                }
            } finally {
                if (!cancelled) {
                    setCheckingAccess(false);
                }
            }
        };

        loadInitialAccess();
        return () => {
            cancelled = true;
        };
    }, [user, isAuthenticated, isLoading, fetchAccessStatus]);

    useEffect(() => {
        const onAccessChanged = async (event) => {
            if (event?.detail) {
                setAccessData((prev) => ({ ...(prev || {}), ...event.detail }));
                if (event.detail.billing_ready !== true) {
                    try {
                        const data = await fetchAccessStatus();
                        if (data) {
                            setAccessData(data);
                        }
                    } catch (error) {
                        console.error('Error refreshing access status after billing update:', error);
                    }
                }
                return;
            }
            if (isLoading || !isAuthenticated || !user?.email) {
                return;
            }
            try {
                const data = await fetchAccessStatus();
                if (data) {
                    setAccessData(data);
                }
            } catch (error) {
                console.error('Error refreshing access status:', error);
            }
        };

        window.addEventListener('keyflo-access-changed', onAccessChanged);
        return () => window.removeEventListener('keyflo-access-changed', onAccessChanged);
    }, [user, isAuthenticated, isLoading, fetchAccessStatus]);

    if (isLoading || checkingAccess) {
        return <LoadingSpinner />;
    }

    if (!isAuthenticated) {
        const path = location.pathname || '';
        // Auth0 session is not an entitlement. Do not render waitlist/profile
        // chrome before access-status has a waitlist or users/TT row.
        if (path === '/profile' || path.startsWith('/profile') || path === '/waitlist') {
            return <Signup />;
        }
        return children;
    }

    const status = accessData?.status;
    const billingReady = accessData?.billing_ready === true;
    const paymentUserType = accessData?.payment_user_type;
    const billingStatus = accessData?.billing_status;
    const isBeta = paymentUserType === 'beta_tester';

    const isApproved = ['APPROVED', 'SUBSCRIBED', 'APPROVED_NON_SUBSCRIBED'].includes(status);
    const isNonBillableParticipant = status === 'APPROVED_NON_SUBSCRIBED';
    const isProfilePath = location.pathname === '/profile' || location.pathname.startsWith('/profile');

    if (isApproved && !isNonBillableParticipant && !billingReady && !isBeta && billingStatus === 'past_due') {
        if (isProfilePath) {
            return (
                <>
                    <BillingGraceBanner gracePeriodEndsAt={accessData?.grace_period_ends_at} />
                    {children}
                </>
            );
        }
        return <PaymentRequired />;
    }

    if (isApproved && !isNonBillableParticipant && !billingReady && !isBeta) {
        if (isProfilePath) {
            return children;
        }
        return <PaymentRequired />;
    }

    // Waitlist users may complete profile (no billing) on /profile.
    if (status === 'WAITLIST') {
        if (isProfilePath) {
            return children;
        }
        return <Waitlist />;
    }

    if (isApproved) {
        return (
            <>
                {billingStatus === 'grace_period' && (
                    <BillingGraceBanner gracePeriodEndsAt={accessData?.grace_period_ends_at} />
                )}
                {children}
            </>
        );
    }

    if (status === 'UNAVAILABLE') {
        return (
            <Signup
                gatekeeperUnavailable
                onRetryAccess={() => {
                    setCheckingAccess(true);
                    fetchAccessStatus()
                        .then((data) => {
                            if (data?.status) {
                                setAccessData(data);
                            } else {
                                setAccessData({ status: 'UNAVAILABLE' });
                            }
                        })
                        .catch((error) => {
                            console.error('Error checking access status:', error);
                            setAccessData({ status: 'UNAVAILABLE' });
                        })
                        .finally(() => setCheckingAccess(false));
                }}
            />
        );
    }

    if (status === 'UNKNOWN') {
        return <Signup gatekeeperUnknown />;
    }

    return <LoadingSpinner />;
};

export default Gatekeeper;
